import React from 'react';
import type { Service, VehiculeConfig } from '../../../../shared/types/config';

/** Sélecteur de quantités véhicules groupées par service, extrait de InterventionsCreate.tsx. */

interface VehiculesByServiceEntry {
  service: Service;
  byType: Record<string, VehiculeConfig[]>;
  types: string[];
}

interface VehiculesByServicePickerProps {
  loadingVehicules: boolean;
  vehiculesByService: VehiculesByServiceEntry[];
  vehiculeSearchQuery: string;
  setVehiculeSearchQuery: (v: string) => void;
  openServiceId: number | null;
  setOpenServiceId: (id: number | null) => void;
  vehiculeQuantites: Record<number, number>;
  setVehiculeQuantite: (id: number, qty: number) => void;
}

const VehiculesByServicePicker: React.FC<VehiculesByServicePickerProps> = ({
  loadingVehicules,
  vehiculesByService,
  vehiculeSearchQuery,
  setVehiculeSearchQuery,
  openServiceId,
  setOpenServiceId,
  vehiculeQuantites,
  setVehiculeQuantite,
}) => (
  <div className="md:col-span-2">
    <label className="block text-sm font-medium text-gray-300 mb-2">Véhicules par service</label>
    {loadingVehicules ? (
      <p className="text-sm text-gray-500">Chargement des services et véhicules...</p>
    ) : vehiculesByService.length === 0 ? (
      <p className="text-sm text-gray-500">Aucun service ou véhicule configuré.</p>
    ) : (
      <>
        <input
          type="text"
          value={vehiculeSearchQuery}
          onChange={(e) => setVehiculeSearchQuery(e.target.value)}
          placeholder="Rechercher un véhicule ou un type..."
          className="w-full mb-3 px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <div className="space-y-2 rounded-lg border border-gray-700 overflow-hidden">
          {vehiculesByService.map(({ service, byType, types }) => {
            const searchLower = vehiculeSearchQuery.trim().toLowerCase();
            const filterV = (list: VehiculeConfig[]) =>
              !searchLower
                ? list
                : list.filter(
                    (v) =>
                      v.nom.toLowerCase().includes(searchLower) ||
                      (v.type || '').toLowerCase().includes(searchLower)
                  );
            const typesFiltered = types.filter((t) => filterV(byType[t] || []).length > 0);
            const count = typesFiltered.reduce(
              (acc, t) => acc + filterV(byType[t] || []).length,
              0
            );
            if (count === 0) return null;
            const isOpen = openServiceId === service.id;
            return (
              <div key={service.id} className="bg-gray-800/50">
                <button
                  type="button"
                  onClick={() => setOpenServiceId(isOpen ? null : service.id)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left text-gray-200 hover:bg-gray-700/50 transition-colors"
                >
                  <span className="font-medium">{service.nom}</span>
                  <span className="text-sm text-gray-400">
                    {count} véhicule{count !== 1 ? 's' : ''}
                  </span>
                  <svg
                    className={`w-5 h-5 text-gray-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isOpen && (
                  <div className="border-t border-gray-700 px-4 py-3 space-y-4">
                    {typesFiltered.map((type) => {
                      const list = filterV(byType[type] || []);
                      return (
                        <div key={type}>
                          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                            {type}
                          </div>
                          <ul className="space-y-2">
                            {list.map((v, i) => (
                              <li
                                key={v.id ?? i}
                                className="flex items-center justify-between gap-3 text-sm text-gray-200"
                              >
                                <span className="min-w-0 truncate">{v.nom}</span>
                                <input
                                  type="number"
                                  min={0}
                                  value={vehiculeQuantites[v.id ?? 0] ?? 0}
                                  onChange={(e) =>
                                    setVehiculeQuantite(v.id ?? 0, parseInt(e.target.value, 10) || 0)
                                  }
                                  className="w-20 px-2 py-1 text-sm bg-gray-800/50 border border-gray-600 rounded text-white focus:ring-1 focus:ring-primary-500 shrink-0"
                                />
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Quantité requise pour cette intervention (0 par défaut).
        </p>
      </>
    )}
  </div>
);

export default VehiculesByServicePicker;
